/**
 * Notification API Hooks
 * React hooks for notification service endpoints
 */

import { useCallback } from 'react';
import { useFetch, buildUrl } from './useApi';
import { NOTIFICATION_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';
import type {
  NotificationsResponse,
  NotificationResponse,
  UnreadCountResponse,
  ActiveNotificationsResponse,
  ManageNotificationDTO,
} from './notificationTypes';
import type { ApiResponse } from './types';

/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

// Helper function to get user type from user role
function getUserTypeFromRole(role?: string): string {
  if (!role) return 'ADMIN';
  const roleUpper = role.toUpperCase();
  if (roleUpper.includes('ADMIN') || roleUpper === 'SUPER_ADMIN') return 'ADMIN';
  if (roleUpper.includes('DRIVER')) return 'DRIVER';
  if (roleUpper.includes('PASSENGER') || roleUpper.includes('RIDER')) return 'RIDER';
  return 'ADMIN';
}

/**
 * Hook to fetch all notifications for a user
 * GET /notification/user/{userId}/{userType}
 */
export function useUserNotifications(
  userId: number | undefined,
  userType: string = 'ADMIN',
  options: {
    enabled?: boolean;
    onSuccess?: (data: ManageNotificationDTO[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(NOTIFICATION_API_BASE_URL, `/notification/user/${userId}/${userType}`);

  const response = useFetch<NotificationsResponse>(url, {
    enabled: options.enabled !== false && !!userId,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data || []),
    onError: options.onError,
  });

  // Extract the actual data from the response wrapper
  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to fetch unread notifications for a user
 * GET /notification/user/{userId}/{userType}/unread
 */
export function useUnreadNotifications(
  userId: number | undefined,
  userType: string = 'ADMIN',
  options: {
    enabled?: boolean;
    onSuccess?: (data: ManageNotificationDTO[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(NOTIFICATION_API_BASE_URL, `/notification/user/${userId}/${userType}/unread`);

  const response = useFetch<NotificationsResponse>(url, {
    enabled: options.enabled !== false && !!userId,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data || []),
    onError: options.onError,
  });

  // Extract the actual data from the response wrapper
  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to fetch unread count for a user
 * GET /notification/user/{userId}/{userType}/unread/count
 */
export function useUnreadCount(
  userId: number | undefined,
  userType: string = 'ADMIN',
  options: {
    enabled?: boolean;
    onSuccess?: (data: number) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(NOTIFICATION_API_BASE_URL, `/notification/user/${userId}/${userType}/unread/count`);

  const response = useFetch<UnreadCountResponse>(url, {
    enabled: options.enabled !== false && !!userId,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data ?? 0),
    onError: options.onError,
  });

  // Extract the actual data from the response wrapper
  return {
    ...response,
    data: response.data?.data ?? 0,
  };
}

/**
 * Hook to fetch active notifications with server-side pagination and search
 * GET /notification/active?page={page}&size={size}&search={search}
 */
export function useActiveNotifications(params: {
  page?: number;
  size?: number;
  search?: string;
  enabled?: boolean;
  onSuccess?: (data: ManageNotificationDTO[]) => void;
  onError?: (error: Error) => void;
}) {
  const { page = 0, size = 10, search } = params;

  const queryParams = new URLSearchParams();
  queryParams.set('page', String(page));
  queryParams.set('size', String(size));
  queryParams.set('sort', 'createdDate,desc');
  if (search) queryParams.set('search', search);

  const url = buildUrl(NOTIFICATION_API_BASE_URL, `/notification/active?${queryParams.toString()}`);

  const response = useFetch<any>(url, {
    enabled: params.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => {
      const parsed = extractPagedData(data);
      params.onSuccess?.(parsed.content);
    },
    onError: params.onError,
  });

  const parsed = extractPagedData(response.data);

  return {
    ...response,
    data: parsed.content,
    totalElements: parsed.totalElements,
    totalPages: parsed.totalPages,
  };
}

/** Extract paged notification data from the API response
 *  Response shape: { status, httpStatus, message, data: { content: [...], page: { size, number, totalElements, totalPages } } }
 */
function extractPagedData(data: any): { content: ManageNotificationDTO[]; totalElements: number; totalPages: number } {
  const empty = { content: [], totalElements: 0, totalPages: 0 };
  if (!data) return empty;

  const inner = data.data; // unwrap ResponseObject
  if (!inner) return empty;

  // Spring Boot 3.x: { content: [...], page: { totalElements, totalPages } }
  if (Array.isArray(inner.content) && inner.page) {
    return { content: inner.content, totalElements: inner.page.totalElements || 0, totalPages: inner.page.totalPages || 0 };
  }
  // Spring Boot 2.x: { content: [...], totalElements, totalPages }
  if (Array.isArray(inner.content)) {
    return { content: inner.content, totalElements: inner.totalElements || 0, totalPages: inner.totalPages || 0 };
  }
  // Flat array
  if (Array.isArray(inner)) {
    return { content: inner, totalElements: inner.length, totalPages: 1 };
  }
  return empty;
}

/**
 * Hook to mark a notification as read
 * PUT /notification/mark-read/{id}
 */
export function useMarkAsRead(options: {
  onSuccess?: (data: NotificationResponse) => void;
  onError?: (error: Error) => void;
}) {
  return useCallback(async (notificationId: number): Promise<NotificationResponse> => {
    const url = buildUrl(NOTIFICATION_API_BASE_URL, `/notification/mark-read/${notificationId}`);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notification as read: ${response.statusText}`);
      }

      const data = await response.json();
      options.onSuccess?.(data);
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to mark notification as read');
      options.onError?.(err);
      throw err;
    }
  }, [options]);
}

/**
 * Hook to mark all notifications as read
 * PUT /notification/mark-all-as-read
 */
export function useMarkAllAsRead(options: {
  onSuccess?: (data: NotificationResponse) => void;
  onError?: (error: Error) => void;
}) {
  return useCallback(async (): Promise<NotificationResponse> => {
    const url = buildUrl(NOTIFICATION_API_BASE_URL, `/notification/mark-all-as-read`);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notifications as read: ${response.statusText}`);
      }

      const data = await response.json();
      options.onSuccess?.(data);
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to mark notifications as read');
      options.onError?.(err);
      throw err;
    }
  }, [options]);
}
